import React from 'react'
import { Box, Divider, MenuItem, Typography } from '@material-ui/core'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormGroup } from '../../../../form/FormGroup'
import { ControlledTextField } from '../../../../form/ControlledTextField'
import { validators } from '../../../../../validation/validators'
import { HeatingOrCoolingSource } from '../../../../../generated/graphql'
import { powerUnit, temperatureUnit } from '../../../../../util/formatters'
import { InternalFormValues } from '../../form-values'
import { StandardHeatingOrCoolingSource } from './StandardHeatingOrCoolingSource'

export function HeatExhaustOptions(): React.ReactElement {
  const { t } = useTranslation()
  const { control, errors } = useFormContext<InternalFormValues>()

  const freeCoolingSource = useWatch<HeatingOrCoolingSource>({
    control,
    name: 'parameters.hybridHeatPump.freeCoolingSource',
  })
  const coolingSource = useWatch<HeatingOrCoolingSource>({
    control,
    name: 'parameters.hybridHeatPump.coolingSource',
  })

  const availableFreeCoolingSources = Object.values(
    HeatingOrCoolingSource,
  ).filter((source) => source !== HeatingOrCoolingSource.EnergyBus)

  return (
    <Box flexGrow={1} display="flex" flexDirection="column">
      <FormGroup>
        <ControlledTextField
          control={control}
          name="parameters.hybridHeatPump.freeCoolingSource"
          rules={{ ...validators.required }}
          error={errors.parameters?.hybridHeatPump?.freeCoolingSource}
          label={t('createProvider.forms.hybridHeatPump.freeCoolingSource')}
          select
        >
          {availableFreeCoolingSources.map((source) => (
            <MenuItem key={source} value={source}>
              {t(`heatingOrCoolingSource.${source}`)}
            </MenuItem>
          ))}
        </ControlledTextField>
      </FormGroup>

      {freeCoolingSource === HeatingOrCoolingSource.Ground && (
        <FormGroup>
          <ControlledTextField
            control={control}
            name="parameters.hybridHeatPump.freeGroundCoolingMaxPower"
            rules={{
              ...validators.maxPower,
            }}
            error={errors.parameters?.hybridHeatPump?.freeGroundCoolingMaxPower}
            label={t(
              'createProvider.forms.hybridHeatPump.freeGroundCoolingMaxPower',
            )}
            InputProps={{
              endAdornment: <Typography>{powerUnit}</Typography>,
            }}
          />
        </FormGroup>
      )}

      {freeCoolingSource === HeatingOrCoolingSource.Air && (
        <FormGroup>
          <ControlledTextField
            control={control}
            name="parameters.hybridHeatPump.freeAirCoolingMaxTemperature"
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            error={
              errors.parameters?.hybridHeatPump?.freeAirCoolingMaxTemperature
            }
            label={t(
              'createProvider.forms.hybridHeatPump.freeAirCoolingMaxTemperature',
            )}
            InputProps={{
              endAdornment: <Typography>{temperatureUnit}</Typography>,
            }}
          />
        </FormGroup>
      )}

      {(freeCoolingSource === HeatingOrCoolingSource.Ground ||
        freeCoolingSource === HeatingOrCoolingSource.Air) && (
        <FormGroup>
          <ControlledTextField
            control={control}
            name="parameters.hybridHeatPump.freeCoolingEer"
            rules={{
              ...validators.required,
              ...validators.eer,
            }}
            error={errors.parameters?.hybridHeatPump?.freeCoolingEer}
            label={t('createProvider.forms.hybridHeatPump.freeCoolingEer')}
          />
        </FormGroup>
      )}

      <Box mb={2}>
        <Divider />
      </Box>

      <FormGroup>
        <StandardHeatingOrCoolingSource
          control={control}
          name="parameters.hybridHeatPump.coolingSource"
          error={errors.parameters?.hybridHeatPump?.coolingSource}
          label={t('createProvider.forms.hybridHeatPump.coolingSource')}
        />
      </FormGroup>

      {coolingSource === HeatingOrCoolingSource.Ground && (
        <FormGroup>
          <ControlledTextField
            control={control}
            name="parameters.hybridHeatPump.groundExhaustTemperature"
            rules={{
              ...validators.required,
              ...validators.decimal,
            }}
            error={errors.parameters?.hybridHeatPump?.groundExhaustTemperature}
            label={t(
              'createProvider.forms.hybridHeatPump.groundExhaustTemperature',
            )}
            InputProps={{
              endAdornment: <Typography>{temperatureUnit}</Typography>,
            }}
          />
        </FormGroup>
      )}
    </Box>
  )
}
